import React, { useState, useEffect } from 'react';
import {
  User,
  Bell,
  Shield,
  Palette,
  Globe,
  Monitor,
  Moon,
  Sun,
  Smartphone,
  Mail,
  Lock,
  Key,
  Eye,
  EyeOff,
  Save,
  RefreshCw,
  Check,
  X,
  Trash2,
  LogOut,
  Clock,
  MapPin,
  AlertTriangle,
  Copy,
  ChevronRight,
} from 'lucide-react';
import { useAuth } from 'hooks/useAuth';
import { ProviderAccessCard } from './ProviderAccessCard';
import OrgCategoryCard from 'components/Compliance/OrgCategoryCard';
import { traiComplianceOn } from 'components/Compliance/CallCategoryField';
import { useTheme } from 'contexts/ThemeContext';
import { preferencesService, securityService } from 'api/services/settings.service';
import {
  UserPreferences,
  SecuritySettings,
  ActiveSession,
  ThemeMode,
  PasswordChangeRequest,
} from 'types/settings';

type SettingsTab = 'profile' | 'notifications' | 'security' | 'appearance' | 'sessions';

const Settings: React.FC = () => {
  const { user } = useAuth();
  const { theme: currentTheme, setTheme } = useTheme();
  const [activeTab, setActiveTab] = useState<SettingsTab>('profile');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [preferences, setPreferences] = useState<UserPreferences | null>(null);
  const [security, setSecurity] = useState<SecuritySettings | null>(null);
  const [sessions, setSessions] = useState<ActiveSession[]>([]);
  const [sessionStats, setSessionStats] = useState<{ totalSessions: number; maxSessionsAllowed: number; availableSlots: number } | null>(null);
  const [successMessage, setSuccessMessage] = useState('');

  // Password change state
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [passwordForm, setPasswordForm] = useState<PasswordChangeRequest>({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  // 2FA state
  const [show2FASetup, setShow2FASetup] = useState(false);
  const [twoFactorCode, setTwoFactorCode] = useState('');
  const [backupCodes, setBackupCodes] = useState<string[]>([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [prefsData, securityData, sessionsResponse] = await Promise.all([
        preferencesService.get().catch(() => null),
        securityService.getSettings().catch(() => null),
        securityService.getSessions().catch(() => null),
      ]);

      if (prefsData) setPreferences(prefsData);
      if (securityData) setSecurity(securityData);
      if (sessionsResponse) {
        setSessions(sessionsResponse.sessions || []);
        setSessionStats({
          totalSessions: sessionsResponse.totalSessions,
          maxSessionsAllowed: sessionsResponse.maxSessionsAllowed,
          availableSlots: sessionsResponse.availableSlots,
        });
      }
    } catch (error) {
      console.error('Failed to load settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const savePreferences = async () => {
    if (!preferences) return;
    try {
      setSaving(true);
      await preferencesService.update(preferences);
      showSuccess('Preferences saved successfully');
    } catch (error) {
      console.error('Failed to save preferences:', error);
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordChange = async () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      alert('Passwords do not match');
      return;
    }
    try {
      setSaving(true);
      await securityService.changePassword(passwordForm);
      showSuccess('Password changed successfully');
      setShowPasswordForm(false);
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      console.error('Failed to change password:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleRevokeSession = async (sessionId: string) => {
    try {
      await securityService.revokeSession(sessionId);
      setSessions(sessions.filter((s) => s.id !== sessionId));
      showSuccess('Session revoked');
    } catch (error) {
      console.error('Failed to revoke session:', error);
    }
  };

  const handleRevokeAllSessions = async () => {
    if (!confirm('This will log you out from all other devices. Continue?')) return;
    try {
      await securityService.revokeAllSessions();
      // Reload sessions to get updated list
      const sessionsResponse = await securityService.getSessions();
      if (sessionsResponse) {
        setSessions(sessionsResponse.sessions || []);
        setSessionStats({
          totalSessions: sessionsResponse.totalSessions,
          maxSessionsAllowed: sessionsResponse.maxSessionsAllowed,
          availableSlots: sessionsResponse.availableSlots,
        });
      }
      showSuccess('All other sessions revoked');
    } catch (error) {
      console.error('Failed to revoke sessions:', error);
    }
  };

  const showSuccess = (message: string) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const tabs = [
    { id: 'profile' as SettingsTab, label: 'Profile', icon: User },
    { id: 'notifications' as SettingsTab, label: 'Notifications', icon: Bell },
    { id: 'security' as SettingsTab, label: 'Security', icon: Shield },
    { id: 'appearance' as SettingsTab, label: 'Appearance', icon: Palette },
    { id: 'sessions' as SettingsTab, label: 'Sessions', icon: Monitor },
  ];

  const timezones = [
    'UTC',
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'Europe/London',
    'Europe/Paris',
    'Asia/Tokyo',
    'Asia/Shanghai',
    'Asia/Kolkata',
    'Australia/Sydney',
  ];

  const languages = [
    { code: 'en', name: 'English' },
    { code: 'es', name: 'Spanish' },
    { code: 'fr', name: 'French' },
    { code: 'de', name: 'German' },
    { code: 'pt', name: 'Portuguese' },
    { code: 'zh', name: 'Chinese' },
    { code: 'ja', name: 'Japanese' },
    { code: 'hi', name: 'Hindi' },
  ];

  if (loading) {
    return (
      <div className="p-6 flex justify-center items-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="p-4 lg:p-6">
      {/* Header */}
      <div className="mb-4 lg:mb-6">
        <h1 className="text-xl lg:text-2xl font-bold text-gray-900 dark:text-white">Settings</h1>
        <p className="text-sm lg:text-base text-gray-600 dark:text-gray-400">Manage your account settings and preferences</p>
      </div>

      {/* Business-managed controls. The first of what is meant to grow into the

          place a business governs its relationship with its provider — what they

          may do, and later what the business can ask them for. Shown only for a

          BUSINESS organisation: a service provider has no provider above it. */}

      {traiComplianceOn() && <OrgCategoryCard />}

      {(() => {

        const org: any = (user as any)?.organizations?.find(

          (o: any) => (o.type || "").toUpperCase() === "BUSINESS") || null;

        if (!org) return null;

        return (

          <div className="mb-4 lg:mb-6">

            <ProviderAccessCard

              organizationId={org.id}

              organizationName={org.name}

              canChange={true}

            />

          </div>

        );

      })()}


      {/* Success Message */}
      {successMessage && (
        <div className="mb-4 p-3 lg:p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg flex items-center gap-2 text-green-800 dark:text-green-400 text-sm">
          <Check className="w-5 h-5 flex-shrink-0" />
          {successMessage}
        </div>
      )}

      {/* Mobile: Horizontal Tab Scroll */}
      <div className="lg:hidden mb-4 -mx-4 px-4">
        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-shrink-0 flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition ${
                  activeTab === tab.id
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300'
                }`}
              >
                <Icon className="w-4 h-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="flex gap-6">
        {/* Sidebar - Hidden on Mobile */}
        <div className="hidden lg:block w-64 flex-shrink-0">
          <nav className="bg-white dark:bg-gray-800 rounded-lg shadow p-2 space-y-1 sticky top-4">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition ${
                    activeTab === tab.id
                      ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400'
                      : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="font-medium">{tab.label}</span>
                </button>
              );
            })}
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1 bg-white dark:bg-gray-800 rounded-lg shadow min-w-0">
          {/* Profile Tab */}
          {activeTab === 'profile' && (
            <div className="p-4 lg:p-6">
              <h2 className="text-lg font-semibold mb-4 lg:mb-6 text-gray-900 dark:text-white">Profile Settings</h2>

              <div className="space-y-5 lg:space-y-6">
                {/* User Info (Read Only) */}
                <div className="flex flex-col sm:flex-row items-center gap-4 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                  <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center text-white text-2xl font-bold flex-shrink-0">
                    {user?.firstName?.charAt(0) || user?.name?.charAt(0) || '?'}
                  </div>
                  <div className="text-center sm:text-left">
                    <p className="font-semibold text-lg text-gray-900 dark:text-white">
                      {user?.firstName && user?.lastName
                        ? `${user.firstName} ${user.lastName}`
                        : user?.name || 'User'}
                    </p>
                    <p className="text-gray-500 dark:text-gray-400 text-sm">{user?.email}</p>
                    <p className="text-sm text-gray-400 dark:text-gray-500 capitalize">{user?.role?.replace('_', ' ')}</p>
                  </div>
                </div>

                {/* Timezone & Language - Grid on larger screens */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      <Globe className="w-4 h-4 inline mr-2" />
                      Timezone
                    </label>
                    <select
                      value={preferences?.timezone || 'UTC'}
                      onChange={(e) =>
                        setPreferences(preferences ? { ...preferences, timezone: e.target.value } : null)
                      }
                      className="w-full px-4 py-2 border dark:border-gray-600 dark:bg-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                    >
                      {timezones.map((tz) => (
                        <option key={tz} value={tz}>
                          {tz}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      <Globe className="w-4 h-4 inline mr-2" />
                      Language
                    </label>
                    <select
                      value={preferences?.language || 'en'}
                      onChange={(e) =>
                        setPreferences(preferences ? { ...preferences, language: e.target.value } : null)
                      }
                      className="w-full px-4 py-2 border dark:border-gray-600 dark:bg-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                    >
                      {languages.map((lang) => (
                        <option key={lang.code} value={lang.code}>
                          {lang.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Date & Time Format - Grid on larger screens */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Date Format</label>
                    <select
                      value={preferences?.dateFormat || 'MM/DD/YYYY'}
                      onChange={(e) =>
                        setPreferences(preferences ? { ...preferences, dateFormat: e.target.value } : null)
                      }
                      className="w-full px-4 py-2 border dark:border-gray-600 dark:bg-gray-700 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                    >
                      <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                      <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                      <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Time Format</label>
                    <div className="flex gap-4 flex-wrap">
                      <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                        <input
                          type="radio"
                          name="timeFormat"
                          value="12h"
                          checked={preferences?.timeFormat === '12h'}
                          onChange={() =>
                            setPreferences(preferences ? { ...preferences, timeFormat: '12h' } : null)
                          }
                          className="text-blue-600"
                        />
                        12-hour
                      </label>
                      <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                        <input
                          type="radio"
                          name="timeFormat"
                          value="24h"
                          checked={preferences?.timeFormat === '24h'}
                          onChange={() =>
                            setPreferences(preferences ? { ...preferences, timeFormat: '24h' } : null)
                          }
                          className="text-blue-600"
                        />
                        24-hour
                      </label>
                    </div>
                  </div>
                </div>

                <button
                  onClick={savePreferences}
                  disabled={saving}
                  className="tl-btn tl-btn-primary tl-btn-sm w-full sm:w-auto disabled:opacity-50"
                >
                  {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Save Changes
                </button>
              </div>
            </div>
          )}

          {/* Notifications Tab */}
          {activeTab === 'notifications' && preferences && (
            <div className="p-6">
              <h2 className="text-lg font-semibold mb-6 text-gray-900 dark:text-white">Notification Preferences</h2>

              <div className="space-y-8">
                {/* Email Notifications */}
                <div>
                  <h3 className="text-md font-medium text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                    <Mail className="w-5 h-5" />
                    Email Notifications
                  </h3>
                  <div className="space-y-3 ml-7">
                    {[
                      { key: 'campaignUpdates', label: 'Campaign Updates', desc: 'Get notified when campaigns start, complete, or fail' },
                      { key: 'billingAlerts', label: 'Billing Alerts', desc: 'Low balance warnings and payment confirmations' },
                      { key: 'securityAlerts', label: 'Security Alerts', desc: 'Login attempts and security-related notifications' },
                      { key: 'weeklyReports', label: 'Weekly Reports', desc: 'Weekly summary of your account activity' },
                      { key: 'marketingEmails', label: 'Marketing Emails', desc: 'Product updates and promotional content' },
                    ].map((item) => (
                      <label key={item.key} className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={preferences.notifications?.email?.[item.key as keyof typeof preferences.notifications.email] ?? false}
                          onChange={(e) =>
                            setPreferences({
                              ...preferences,
                              notifications: {
                                ...preferences.notifications,
                                email: {
                                  ...preferences.notifications?.email,
                                  [item.key]: e.target.checked,
                                },
                              },
                            })
                          }
                          className="mt-1 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                        />
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">{item.label}</p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">{item.desc}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Push Notifications */}
                <div>
                  <h3 className="text-md font-medium text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                    <Bell className="w-5 h-5" />
                    Push Notifications
                  </h3>
                  <div className="space-y-3 ml-7">
                    {[
                      { key: 'callEvents', label: 'Call Events', desc: 'Incoming calls, missed calls, voicemails' },
                      { key: 'messageEvents', label: 'Message Events', desc: 'New messages across all channels' },
                      { key: 'campaignEvents', label: 'Campaign Events', desc: 'Campaign status changes' },
                      { key: 'billingAlerts', label: 'Billing Alerts', desc: 'Critical billing notifications' },
                      { key: 'systemAlerts', label: 'System Alerts', desc: 'Important system notifications' },
                    ].map((item) => (
                      <label key={item.key} className="flex items-start gap-3 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={preferences.notifications?.push?.[item.key as keyof typeof preferences.notifications.push] ?? false}
                          onChange={(e) =>
                            setPreferences({
                              ...preferences,
                              notifications: {
                                ...preferences.notifications,
                                push: {
                                  ...preferences.notifications?.push,
                                  [item.key]: e.target.checked,
                                },
                              },
                            })
                          }
                          className="mt-1 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                        />
                        <div>
                          <p className="font-medium text-gray-900 dark:text-white">{item.label}</p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">{item.desc}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {/* In-App Notifications */}
                <div>
                  <h3 className="text-md font-medium text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                    <Smartphone className="w-5 h-5" />
                    In-App Notifications
                  </h3>
                  <div className="space-y-3 ml-7">
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.notifications?.inApp?.showBadge ?? true}
                        onChange={(e) =>
                          setPreferences({
                            ...preferences,
                            notifications: {
                              ...preferences.notifications,
                              inApp: {
                                ...preferences.notifications?.inApp,
                                showBadge: e.target.checked,
                              },
                            },
                          })
                        }
                        className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-gray-900 dark:text-white">Show notification badge</span>
                    </label>
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={preferences.notifications?.inApp?.playSound ?? true}
                        onChange={(e) =>
                          setPreferences({
                            ...preferences,
                            notifications: {
                              ...preferences.notifications,
                              inApp: {
                                ...preferences.notifications?.inApp,
                                playSound: e.target.checked,
                              },
                            },
                          })
                        }
                        className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-gray-900 dark:text-white">Play notification sound</span>
                    </label>
                  </div>
                </div>

                <button
                  onClick={savePreferences}
                  disabled={saving}
                  className="tl-btn tl-btn-primary disabled:opacity-50"
                >
                  {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Save Changes
                </button>
              </div>
            </div>
          )}

          {/* Security Tab */}
          {activeTab === 'security' && (
            <div className="p-6">
              <h2 className="text-lg font-semibold mb-6 text-gray-900 dark:text-white">Security Settings</h2>

              <div className="space-y-6">
                {/* Password Change */}
                <div className="p-4 border dark:border-gray-700 rounded-lg">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h3 className="font-medium flex items-center gap-2 text-gray-900 dark:text-white">
                        <Lock className="w-5 h-5" />
                        Password
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Last changed: {security?.lastPasswordChange ? new Date(security.lastPasswordChange).toLocaleDateString() : 'Never'}
                      </p>
                    </div>
                    <button
                      onClick={() => setShowPasswordForm(!showPasswordForm)}
                      className="px-4 py-2 border dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      Change Password
                    </button>
                  </div>

                  {showPasswordForm && (
                    <div className="space-y-4 pt-4 border-t dark:border-gray-700">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Current Password</label>
                        <div className="relative">
                          <input
                            type={showPasswords.current ? 'text' : 'password'}
                            value={passwordForm.currentPassword}
                            onChange={(e) => setPasswordForm({ ...passwordForm, currentPassword: e.target.value })}
                            className="w-full max-w-md px-4 py-2 border dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                          >
                            {showPasswords.current ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                          </button>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">New Password</label>
                        <div className="relative">
                          <input
                            type={showPasswords.new ? 'text' : 'password'}
                            value={passwordForm.newPassword}
                            onChange={(e) => setPasswordForm({ ...passwordForm, newPassword: e.target.value })}
                            className="w-full max-w-md px-4 py-2 border dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                          >
                            {showPasswords.new ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                          </button>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Confirm New Password</label>
                        <div className="relative">
                          <input
                            type={showPasswords.confirm ? 'text' : 'password'}
                            value={passwordForm.confirmPassword}
                            onChange={(e) => setPasswordForm({ ...passwordForm, confirmPassword: e.target.value })}
                            className="w-full max-w-md px-4 py-2 border dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400"
                          >
                            {showPasswords.confirm ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                          </button>
                        </div>
                      </div>
                      <button
                        onClick={handlePasswordChange}
                        disabled={saving || !passwordForm.currentPassword || !passwordForm.newPassword}
                        className="tl-btn tl-btn-primary disabled:opacity-50"
                      >
                        Update Password
                      </button>
                    </div>
                  )}
                </div>

                {/* Two-Factor Authentication */}
                <div className="p-4 border dark:border-gray-700 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-medium flex items-center gap-2 text-gray-900 dark:text-white">
                        <Key className="w-5 h-5" />
                        Two-Factor Authentication
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        {security?.twoFactorEnabled
                          ? `Enabled via ${security.twoFactorMethod}`
                          : 'Add an extra layer of security to your account'}
                      </p>
                    </div>
                    <span
                      className={`px-3 py-1 rounded-full text-sm font-medium ${
                        security?.twoFactorEnabled
                          ? 'bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-400'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300'
                      }`}
                    >
                      {security?.twoFactorEnabled ? 'Enabled' : 'Disabled'}
                    </span>
                  </div>
                </div>

                {/* Login Notifications */}
                <div className="p-4 border dark:border-gray-700 rounded-lg">
                  <label className="flex items-center justify-between cursor-pointer">
                    <div>
                      <h3 className="font-medium flex items-center gap-2 text-gray-900 dark:text-white">
                        <Bell className="w-5 h-5" />
                        Login Notifications
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Get notified when someone logs into your account
                      </p>
                    </div>
                    <input
                      type="checkbox"
                      checked={security?.loginNotifications ?? false}
                      onChange={() => {}}
                      className="w-5 h-5 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                    />
                  </label>
                </div>

                {/* Session Timeout */}
                <div className="p-4 border dark:border-gray-700 rounded-lg">
                  <h3 className="font-medium flex items-center gap-2 mb-4 text-gray-900 dark:text-white">
                    <Clock className="w-5 h-5" />
                    Session Timeout
                  </h3>
                  <select
                    value={security?.sessionTimeout || 30}
                    onChange={() => {}}
                    className="w-full max-w-xs px-4 py-2 border dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value={15}>15 minutes</option>
                    <option value={30}>30 minutes</option>
                    <option value={60}>1 hour</option>
                    <option value={120}>2 hours</option>
                    <option value={480}>8 hours</option>
                    <option value={1440}>24 hours</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Appearance Tab */}
          {activeTab === 'appearance' && preferences && (
            <div className="p-6">
              <h2 className="text-lg font-semibold mb-6 text-gray-900 dark:text-white">Appearance Settings</h2>

              <div className="space-y-6">
                {/* Theme */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">Theme</label>
                  <div className="grid grid-cols-3 gap-4 max-w-lg">
                    {[
                      { value: 'light' as ThemeMode, label: 'Light', icon: Sun },
                      { value: 'dark' as ThemeMode, label: 'Dark', icon: Moon },
                      { value: 'system' as ThemeMode, label: 'System', icon: Monitor },
                    ].map((theme) => {
                      const Icon = theme.icon;
                      return (
                        <button
                          key={theme.value}
                          onClick={() => {
                            setTheme(theme.value);
                            setPreferences({ ...preferences, theme: theme.value });
                          }}
                          className={`p-4 border-2 rounded-lg flex flex-col items-center gap-2 transition ${
                            currentTheme === theme.value
                              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30'
                              : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500'
                          }`}
                        >
                          <Icon className={`w-6 h-6 ${currentTheme === theme.value ? 'text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-400'}`} />
                          <span className={`text-sm font-medium ${currentTheme === theme.value ? 'text-blue-600 dark:text-blue-400' : 'text-gray-700 dark:text-gray-300'}`}>{theme.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Dashboard Preferences */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Default Dashboard View</label>
                  <select
                    value={preferences.dashboard?.defaultView || 'overview'}
                    onChange={(e) =>
                      setPreferences({
                        ...preferences,
                        dashboard: {
                          ...preferences.dashboard,
                          defaultView: e.target.value as any,
                        },
                      })
                    }
                    className="w-full max-w-md px-4 py-2 border dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="overview">Overview</option>
                    <option value="telephony">Telephony</option>
                    <option value="whatsapp">WhatsApp</option>
                    <option value="sms">SMS</option>
                    <option value="rcs">RCS</option>
                  </select>
                </div>

                {/* Compact Mode */}
                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={preferences.dashboard?.compactMode ?? false}
                    onChange={(e) =>
                      setPreferences({
                        ...preferences,
                        dashboard: {
                          ...preferences.dashboard,
                          compactMode: e.target.checked,
                        },
                      })
                    }
                    className="w-5 h-5 rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500"
                  />
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">Compact Mode</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Show more content with less spacing</p>
                  </div>
                </label>

                {/* Auto Refresh */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Dashboard Auto-Refresh</label>
                  <select
                    value={preferences.dashboard?.refreshInterval || 0}
                    onChange={(e) =>
                      setPreferences({
                        ...preferences,
                        dashboard: {
                          ...preferences.dashboard,
                          refreshInterval: parseInt(e.target.value),
                        },
                      })
                    }
                    className="w-full max-w-md px-4 py-2 border dark:border-gray-600 dark:bg-gray-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value={0}>Manual refresh only</option>
                    <option value={30}>Every 30 seconds</option>
                    <option value={60}>Every minute</option>
                    <option value={300}>Every 5 minutes</option>
                    <option value={600}>Every 10 minutes</option>
                  </select>
                </div>

                <button
                  onClick={savePreferences}
                  disabled={saving}
                  className="tl-btn tl-btn-primary disabled:opacity-50"
                >
                  {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Save Changes
                </button>
              </div>
            </div>
          )}

          {/* Sessions Tab */}
          {activeTab === 'sessions' && (
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Active Sessions</h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Manage your active login sessions</p>
                  {sessionStats && (
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-600 dark:text-gray-400">
                      <span className="flex items-center gap-1">
                        <Monitor className="w-3 h-3" />
                        {sessionStats.totalSessions} / {sessionStats.maxSessionsAllowed} sessions
                      </span>
                      <span className="text-green-600 dark:text-green-400">
                        {sessionStats.availableSlots} slots available
                      </span>
                    </div>
                  )}
                </div>
                {sessions.length > 1 && (
                  <button
                    onClick={handleRevokeAllSessions}
                    className="flex items-center gap-2 px-4 py-2 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20"
                  >
                    <LogOut className="w-4 h-4" />
                    Sign out all other sessions
                  </button>
                )}
              </div>

              <div className="space-y-4">
                {sessions.length === 0 ? (
                  <p className="text-center text-gray-500 dark:text-gray-400 py-8">No active sessions found</p>
                ) : (
                  sessions.map((session) => {
                    // Parse device info for display
                    const deviceType = session.deviceName?.toLowerCase().includes('mobile') ? 'mobile' :
                                      session.deviceName?.toLowerCase().includes('tablet') ? 'tablet' : 'desktop';
                    const displayName = session.deviceName || 'Unknown Device';
                    const displayInfo = session.deviceInfo || 'No device information';

                    return (
                      <div
                        key={session.id}
                        className={`p-4 border rounded-lg ${
                          session.current
                            ? 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20'
                            : 'border-gray-200 dark:border-gray-700'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="p-3 bg-gray-100 dark:bg-gray-700 rounded-lg">
                              {deviceType === 'mobile' ? (
                                <Smartphone className="w-6 h-6 text-gray-600 dark:text-gray-400" />
                              ) : (
                                <Monitor className="w-6 h-6 text-gray-600 dark:text-gray-400" />
                              )}
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <p className="font-medium text-gray-900 dark:text-white">
                                  {displayName}
                                </p>
                                {session.current && (
                                  <span className="px-2 py-0.5 bg-green-100 dark:bg-green-900/40 text-green-800 dark:text-green-400 text-xs rounded-full">
                                    Current session
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 max-w-md truncate">
                                {displayInfo}
                              </p>
                              <div className="flex items-center gap-4 text-sm text-gray-500 dark:text-gray-400 mt-1">
                                <span className="flex items-center gap-1">
                                  <MapPin className="w-3 h-3" />
                                  {session.location || session.ipAddress}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  Last active: {new Date(session.lastAccessedAt).toLocaleString()}
                                </span>
                              </div>
                              <div className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                                Expires: {new Date(session.expiresAt).toLocaleString()}
                              </div>
                            </div>
                          </div>
                          {!session.current && (
                            <button
                              onClick={() => handleRevokeSession(session.id)}
                              className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
                              title="Revoke session"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Settings;
